<template>
  <div class="dash-page">
    <approveLeaveRequestModal v-if="showApproveModal" :selectedLeaveRequest="selectedLeaveRequest"
      @close-approve-leave-request-modal="showApproveModal = false" @refresh-leave-requests="onLeaveDecided()" />
    <rejectLeaveRequestModal v-if="showRejectModal" :selectedLeaveRequest="selectedLeaveRequest"
      @close-reject-leave-request-modal="showRejectModal = false" @refresh-leave-requests="onLeaveDecided()" />

    <!-- Header -->
    <div class="dash-header">
      <h1 class="dash-greeting">{{ greeting }}, {{ $filters.capitalize(account.fname) }}</h1>
    </div>

    <!-- Row 1: attendance + quick actions -->
    <div class="row">
      <div v-if="hasAttendance" class="col-lg-7">
        <attendanceCheckinCard :employeeId="account.id" @attendance-changed="loadStats()" />
      </div>
      <div :class="hasAttendance ? 'col-lg-5' : 'col-lg-12'">
        <sectionCard title="Quick actions" icon="pi-bolt" fill>
          <div class="quick-actions">
            <router-link class="quick-action" :to="{ name: REGISTER_EMPLOYEE_ROUTE }">
              <span class="qa-label">New employee</span>
            </router-link>
            <router-link v-if="hasLeave" class="quick-action" :to="{ name: LEAVES_ROUTE }">
              <span class="qa-label">Leave requests</span>
              <span v-if="leave.pending" class="qa-count">{{ leave.pending }}</span>
            </router-link>
            <router-link v-if="hasPayroll" class="quick-action" :to="{ name: PAYROLL_ROUTE }">
              <span class="qa-label">Run payroll</span>
            </router-link>
            <router-link v-if="hasAttendance" class="quick-action" :to="{ name: ATTENDANCE_ROUTE }">
              <span class="qa-label">Attendance</span>
            </router-link>
            <router-link v-if="hasRecruitment" class="quick-action" :to="{ name: ADD_JOB_POST_ROUTE }">
              <span class="qa-label">Post a job</span>
            </router-link>
            <router-link class="quick-action" :to="{ name: REPORTS_ROUTE }">
              <span class="qa-label">Reports</span>
            </router-link>
          </div>
          <div v-if="hasAttendance || hasLeave || hasRecruitment" class="today-strip">
            <div class="today-strip-title"><i class="pi pi-compass"></i> Today at a glance</div>
            <div v-if="isLoading && !stats">
              <div class="dash-skeleton" style="height: 14px; width: 90%; margin-bottom: 8px;"></div>
              <div class="dash-skeleton" style="height: 14px; width: 60%;"></div>
            </div>
            <div v-else class="today-strip-grid">
              <div v-if="hasAttendance" class="today-cell">
                <span class="today-n">{{ attendance.isAttendanceDay ? attendance.today.present : '—' }}</span>
                <span class="today-l">Checked in</span>
              </div>
              <div v-if="hasLeave" class="today-cell">
                <span class="today-n">{{ leave.onLeaveToday }}</span>
                <span class="today-l">On leave</span>
              </div>
              <div v-if="hasLeave" class="today-cell">
                <span class="today-n">{{ leave.pending }}</span>
                <span class="today-l">Leave to review</span>
              </div>
              <div v-if="hasRecruitment" class="today-cell">
                <span class="today-n">{{ recruitment.newThisWeek }}</span>
                <span class="today-l">Applicants this week</span>
              </div>
            </div>
          </div>
        </sectionCard>
      </div>
    </div>

    <!-- Row 2: attendance overview + pending leave -->
    <div v-if="hasAttendance || hasLeave" class="row">
      <div v-if="hasAttendance" :class="hasLeave ? 'col-lg-8' : 'col-lg-12'">
        <sectionCard title="Attendance overview" :subtitle="attendance.isAttendanceDay ? 'Live status for today and the last 7 working days' : 'Last 7 working days'"
          icon="pi-chart-bar" :loading="isLoading" fill :actionTo="{ name: ATTENDANCE_ROUTE }" actionLabel="Attendance">
          <div v-if="stats && !attendance.isAttendanceDay" class="dash-note">
            <i class="pi pi-info-circle"></i>
            <span v-if="attendance.todayHoliday">Today is a holiday (<strong>{{ attendance.todayHoliday }}</strong>). No attendance is expected.</span>
            <span v-else-if="!headcount.withAttendanceShift">No employees are assigned to an attendance shift yet. <router-link :to="{ name: ATTENDANCE_SETTING_ROUTE }">Set up shifts</router-link>.</span>
            <span v-else>No attendance is scheduled for today.</span>
          </div>
          <div v-else-if="stats" class="stat-strip mb-3">
            <div class="stat-cell cell-green"><span class="n">{{ attendance.today.present }}</span><span class="l">Present</span></div>
            <div class="stat-cell cell-green"><span class="n">{{ attendance.today.onTime }}</span><span class="l">On time</span></div>
            <div class="stat-cell cell-amber"><span class="n">{{ attendance.today.late }}</span><span class="l">Late</span></div>
            <div class="stat-cell cell-blue"><span class="n">{{ attendance.today.onLeave }}</span><span class="l">On leave</span></div>
            <div class="stat-cell cell-red"><span class="n">{{ attendance.today.absent }}</span><span class="l">Not checked in</span></div>
            <div class="stat-cell cell-gray"><span class="n">{{ attendance.today.notYetIn }}</span><span class="l">Shift not started</span></div>
          </div>
          <div v-if="stats && attendance.isAttendanceDay" class="row mb-3">
            <div class="col-md-6">
              <div class="dash-progress-row">
                <div class="dash-progress-head">
                  <span class="name">Attendance rate today</span>
                  <span class="val"><strong>{{ attendance.today.attendanceRate }}%</strong> of {{ attendance.today.expected }}</span>
                </div>
                <div class="dash-progress"><div class="dash-progress-fill" :style="{ width: `${attendance.today.attendanceRate}%` }"></div></div>
              </div>
            </div>
            <div class="col-md-6">
              <div class="dash-progress-row">
                <div class="dash-progress-head">
                  <span class="name">Punctuality this month</span>
                  <span class="val"><strong>{{ attendance.month.punctualityRate }}%</strong> on time · {{ attendance.month.presenceRate }}% presence</span>
                </div>
                <div class="dash-progress"><div class="dash-progress-fill fill-amber" :style="{ width: `${attendance.month.punctualityRate}%` }"></div></div>
              </div>
            </div>
          </div>
          <div v-if="attendance.trend.length">
            <apexchart type="bar" height="240" :options="attendanceTrendOptions" :series="attendanceTrendSeries" />
          </div>
          <emptyState v-else-if="stats" icon="pi-chart-bar" title="No attendance data yet" message="Attendance trends appear once employees start checking in." />
        </sectionCard>
      </div>
      <div v-if="hasLeave" :class="hasAttendance ? 'col-lg-4' : 'col-lg-12'">
        <sectionCard title="Pending leave requests" icon="pi-inbox" noPadding fill
          :badge="leave.pending ? `${leave.pending} pending` : ''" :actionTo="{ name: LEAVES_ROUTE }" actionLabel="View all">
          <ul v-if="leave.pendingRequests.length" class="dash-list">
            <li v-for="request in leave.pendingRequests" :key="request.id" class="dash-list-item">
              <employeeAvatar :employee="request.employee" :size="36" />
              <div class="dash-list-main">
                <p class="dash-list-title">
                  <router-link :to="{ name: EMPLOYEE_GENERAL_ROUTE, params: { id: request.employee?.id } }">{{ fullName(request.employee) }}</router-link>
                </p>
                <p class="dash-list-meta">{{ request.leaveType || 'Leave' }} · {{ formatRange(request.startDate, request.endDate) }} · {{ request.days }} day{{ request.days === 1 ? '' : 's' }}</p>
              </div>
              <div class="dash-list-actions">
                <button class="dash-icon-btn approve" title="Approve" @click="openApprove(request)"><i class="pi pi-check"></i></button>
                <button class="dash-icon-btn reject" title="Reject" @click="openReject(request)"><i class="pi pi-times"></i></button>
              </div>
            </li>
          </ul>
          <emptyState v-else icon="pi-check-circle" title="Inbox zero" message="No leave requests are waiting for your review." />
        </sectionCard>
      </div>
    </div>

    <!-- Row 3: exceptions, payroll, attention -->
    <div class="row">
      <div v-if="hasAttendance || hasLeave" :class="row3Col">
        <sectionCard title="Who is out today" icon="pi-user-minus" noPadding fill>
          <template #tools>
            <div class="mini-tabs">
              <button v-if="hasAttendance" :class="['mini-tab', currentExceptionTab === 'absent' ? 'active' : '']" @click="exceptionTab = 'absent'">Absent <span>{{ attendance.absentCount }}</span></button>
              <button v-if="hasAttendance" :class="['mini-tab', currentExceptionTab === 'late' ? 'active' : '']" @click="exceptionTab = 'late'">Late <span>{{ attendance.lateCount }}</span></button>
              <button v-if="hasLeave" :class="['mini-tab', currentExceptionTab === 'leave' ? 'active' : '']" @click="exceptionTab = 'leave'">Leave <span>{{ leave.onLeaveToday }}</span></button>
            </div>
          </template>
          <template v-if="currentExceptionTab === 'absent'">
            <ul v-if="attendance.absentToday.length" class="dash-list">
              <li v-for="item in attendance.absentToday" :key="item.employee.id" class="dash-list-item">
                <employeeAvatar :employee="item.employee" :size="34" />
                <div class="dash-list-main">
                  <p class="dash-list-title"><router-link :to="{ name: EMPLOYEE_GENERAL_ROUTE, params: { id: item.employee.id } }">{{ fullName(item.employee) }}</router-link></p>
                  <p class="dash-list-meta">{{ item.employee.department || 'No department' }}</p>
                </div>
                <div class="dash-list-side"><span class="muted">Due {{ scheduleTime(item.scheduledIn) }}</span></div>
              </li>
            </ul>
            <emptyState v-else icon="pi-check-circle" :title="attendance.isAttendanceDay ? 'Everyone is accounted for' : 'No attendance today'" message="" />
          </template>
          <template v-else-if="currentExceptionTab === 'late'">
            <ul v-if="attendance.lateToday.length" class="dash-list">
              <li v-for="item in attendance.lateToday" :key="item.employee.id" class="dash-list-item">
                <employeeAvatar :employee="item.employee" :size="34" />
                <div class="dash-list-main">
                  <p class="dash-list-title"><router-link :to="{ name: EMPLOYEE_GENERAL_ROUTE, params: { id: item.employee.id } }">{{ fullName(item.employee) }}</router-link></p>
                  <p class="dash-list-meta">In at {{ $filters.getTimeOnly(item.checkInTime) }} · due {{ scheduleTime(item.scheduledIn) }}</p>
                </div>
                <div class="dash-list-side"><span class="dash-badge badge-soft-amber">+{{ minutesLabel(item.minutesLate) }}</span></div>
              </li>
            </ul>
            <emptyState v-else icon="pi-stopwatch" title="No late arrivals" message="" />
          </template>
          <template v-else>
            <ul v-if="leave.onLeaveTodayList.length" class="dash-list">
              <li v-for="item in leave.onLeaveTodayList" :key="item.id" class="dash-list-item">
                <employeeAvatar :employee="item.employee" :size="34" />
                <div class="dash-list-main">
                  <p class="dash-list-title"><router-link :to="{ name: EMPLOYEE_GENERAL_ROUTE, params: { id: item.employee?.id } }">{{ fullName(item.employee) }}</router-link></p>
                  <p class="dash-list-meta">{{ item.leaveType || 'Leave' }} · back {{ backOnLabel(item.endDate) }}</p>
                </div>
                <div class="dash-list-side"><span class="dash-badge badge-soft-blue">On leave</span></div>
              </li>
            </ul>
            <emptyState v-else icon="pi-sun" title="Nobody is on leave today" message="" />
          </template>
          <template v-if="exceptionOverflow" #footer>
            <router-link :to="{ name: hasAttendance ? ATTENDANCE_ROUTE : LEAVES_ROUTE }" class="dash-card-link">See everyone in {{ hasAttendance ? 'Attendance' : 'Leave' }} <i class="pi pi-arrow-right"></i></router-link>
          </template>
        </sectionCard>
      </div>
      <div v-if="hasPayroll" :class="row3Col">
        <sectionCard title="Payroll overview" :subtitle="payroll.currentPeriod ? `Latest period · ${payroll.currentPeriod.label}` : 'No payroll periods yet'" icon="pi-wallet" fill
          :actionTo="{ name: PAYROLL_ROUTE }" actionLabel="Payroll">
          <template v-if="payroll.currentPeriod">
            <div class="payroll-hero">
              <div>
                <div class="payroll-label">Net payout</div>
                <div class="payroll-value">{{ money(payroll.currentPeriod.net) }}</div>
                <div class="payroll-meta">
                  <span v-if="payroll.netChangePct !== null" :class="['kpi-delta', payroll.netChangePct > 0 ? 'up' : payroll.netChangePct < 0 ? 'down' : 'neutral']">
                    <i :class="['pi', payroll.netChangePct > 0 ? 'pi-arrow-up' : payroll.netChangePct < 0 ? 'pi-arrow-down' : 'pi-minus']"></i>{{ Math.abs(payroll.netChangePct) }}%
                  </span>
                  <span>vs {{ payroll.previousPeriod ? payroll.previousPeriod.label : 'previous' }}</span>
                </div>
              </div>
              <span :class="['dash-badge', payrollStatusClass(payroll.currentPeriod.status)]"><span class="dot"></span>{{ prettify(payroll.currentPeriod.status) }}</span>
            </div>
            <div class="payroll-grid">
              <div class="payroll-cell"><span class="l">Employees</span><span class="v">{{ payroll.currentPeriod.employeeCount }}</span></div>
              <div class="payroll-cell"><span class="l">Gross</span><span class="v">{{ compactMoney(payroll.currentPeriod.gross) }}</span></div>
              <div class="payroll-cell"><span class="l">Tax</span><span class="v">{{ compactMoney(payroll.currentPeriod.tax) }}</span></div>
              <div class="payroll-cell"><span class="l">Pension</span><span class="v">{{ compactMoney(payroll.currentPeriod.pension) }}</span></div>
            </div>
            <div v-if="payroll.trend.length > 1" class="mt-2">
              <apexchart type="bar" height="150" :options="payrollTrendOptions" :series="payrollTrendSeries" />
            </div>
          </template>
          <emptyState v-else icon="pi-wallet" title="No payroll run yet" message="Create a payroll period to see payout analytics here.">
            <router-link :to="{ name: PAYROLL_ROUTE }" class="btn btn-custom-secondary btn-sm">Go to payroll</router-link>
          </emptyState>
          <template v-if="stats" #footer>
            <span v-if="payroll.employeesWithoutSalary" class="text-warning-dark">
              <i class="pi pi-exclamation-triangle"></i> {{ payroll.employeesWithoutSalary }} active employee{{ payroll.employeesWithoutSalary === 1 ? '' : 's' }} without salary setup
            </span>
            <span v-else><i class="pi pi-check-circle" style="color: #2E7918;"></i> All active employees have salary setup</span>
          </template>
        </sectionCard>
      </div>
      <div :class="row3Col">
        <sectionCard title="Needs attention" icon="pi-exclamation-circle" noPadding fill :badge="attentionItems.length ? `${attentionItems.length} area${attentionItems.length === 1 ? '' : 's'}` : ''">
          <ul v-if="attentionItems.length" class="dash-list">
            <li v-for="item in attentionItems" :key="item.key" class="dash-list-item clickable" @click="$router.push(item.to)">
              <span :class="['kpi-icon', 'kpi-icon-sm', `tint-${item.tint}`]"><i :class="['pi', item.icon]"></i></span>
              <div class="dash-list-main">
                <p class="dash-list-title">{{ item.title }}</p>
                <p class="dash-list-meta">{{ item.meta }}</p>
              </div>
              <div class="dash-list-side">
                <span :class="['dash-badge', `badge-soft-${item.tint}`]">{{ item.count }}</span>
              </div>
            </li>
          </ul>
          <emptyState v-else icon="pi-check-circle" title="All caught up" message="No outstanding items need your attention." />
        </sectionCard>
      </div>
    </div>

    <!-- Row 4: headcount, composition, recruitment -->
    <div class="row">
      <div :class="row4Col">
        <sectionCard title="Headcount by department" icon="pi-sitemap" fill :badge="`${headcount.active} active`" :actionTo="{ name: ORGANIZATION_STRUCTURE_ROUTE }" actionLabel="Structure">
          <div v-if="headcount.byDepartment.length">
            <apexchart type="bar" :height="departmentChartHeight" :options="departmentChartOptions" :series="departmentChartSeries" />
          </div>
          <emptyState v-else icon="pi-sitemap" title="No departments yet" message="Assign employees to departments to see the breakdown." />
        </sectionCard>
      </div>
      <div :class="row4Col">
        <sectionCard title="Workforce composition" icon="pi-users" fill>
          <div v-if="headcount.active">
            <apexchart type="donut" height="190" :options="genderChartOptions" :series="genderChartSeries" />
            <div class="composition-title">By contract type</div>
            <div v-for="row in headcount.byContractType" :key="row.type" class="dash-progress-row compact">
              <div class="dash-progress-head">
                <span class="name">{{ row.type === 'unassigned' ? 'No contract' : prettify(row.type) }}</span>
                <span class="val"><strong>{{ row.count }}</strong> · {{ percentOf(row.count, headcount.active) }}%</span>
              </div>
              <div class="dash-progress"><div :class="['dash-progress-fill', row.type === 'unassigned' ? 'fill-gray' : '']" :style="{ width: `${percentOf(row.count, headcount.active)}%` }"></div></div>
            </div>
          </div>
          <emptyState v-else icon="pi-users" title="No active employees" message="Add employees to see workforce analytics." />
        </sectionCard>
      </div>
      <div v-if="hasRecruitment" :class="row4Col">
        <sectionCard title="Recruitment" icon="pi-briefcase" noPadding fill :actionTo="{ name: RECRUITMENT_ROUTE, query: { tab: 'posts' } }" actionLabel="View all">
          <div class="stat-strip strip-inset">
            <div class="stat-cell cell-green"><span class="n">{{ recruitment.openJobPosts }}</span><span class="l">Open posts</span></div>
            <div class="stat-cell cell-amber"><span class="n">{{ recruitment.pendingApplications }}</span><span class="l">Pending</span></div>
            <div class="stat-cell cell-blue"><span class="n">{{ recruitment.interviewStage }}</span><span class="l">Interviewing</span></div>
            <div class="stat-cell cell-purple"><span class="n">{{ recruitment.accepted }}</span><span class="l">Accepted</span></div>
          </div>
          <ul v-if="recruitment.posts.length" class="dash-list">
            <li v-for="post in recruitment.posts" :key="post.id" class="dash-list-item">
              <span class="kpi-icon kpi-icon-sm tint-teal"><i class="pi pi-briefcase"></i></span>
              <div class="dash-list-main">
                <p class="dash-list-title"><router-link :to="{ name: JOB_POST_DETAILS_ROUTE, params: { id: post.id } }">{{ post.title }}</router-link></p>
                <p class="dash-list-meta">{{ post.applications }} applicant{{ post.applications === 1 ? '' : 's' }} · {{ post.pending }} pending · closes {{ post.daysLeft === 0 ? 'today' : `in ${post.daysLeft} days` }}</p>
              </div>
              <div class="dash-list-side"><span class="value">{{ post.applications }}</span></div>
            </li>
          </ul>
          <emptyState v-else icon="pi-megaphone" title="No open job posts" message="Publish a job post to start receiving applications.">
            <router-link :to="{ name: ADD_JOB_POST_ROUTE }" class="btn btn-custom-secondary btn-sm">Post a job</router-link>
          </emptyState>
        </sectionCard>
      </div>
    </div>

    <!-- Row 5: people movements, upcoming, recent hires -->
    <div class="row">
      <div class="col-lg-4">
        <sectionCard title="People movements" icon="pi-arrow-right-arrow-left" noPadding fill>
          <div class="sub-section-title">Contracts ending within 30 days <span class="count">{{ contracts.expiringCount }}</span></div>
          <ul v-if="contracts.expiring.length" class="dash-list">
            <li v-for="item in contracts.expiring" :key="item.id" class="dash-list-item">
              <employeeAvatar :employee="item.employee" :size="34" />
              <div class="dash-list-main">
                <p class="dash-list-title"><router-link :to="{ name: EMPLOYEE_CONTRACTS_ROUTE, params: { id: item.employee.id } }">{{ fullName(item.employee) }}</router-link></p>
                <p class="dash-list-meta">{{ prettify(item.contractType) }} · ends {{ fmtDate(item.endDate) }}</p>
              </div>
              <div class="dash-list-side"><span :class="['dash-badge', item.daysLeft <= 7 ? 'badge-soft-red' : 'badge-soft-amber']">{{ item.daysLeft === 0 ? 'Today' : `${item.daysLeft}d` }}</span></div>
            </li>
          </ul>
          <p v-else class="sub-empty">No contracts are ending soon.</p>
          <div class="sub-section-title">Offboarding in progress <span class="count">{{ offboarding.pending }}</span></div>
          <ul v-if="offboarding.list.length" class="dash-list">
            <li v-for="item in offboarding.list" :key="item.id" class="dash-list-item">
              <employeeAvatar :employee="item.employee" :size="34" />
              <div class="dash-list-main">
                <p class="dash-list-title"><router-link :to="{ name: EMPLOYEE_OFFBOARDING_ROUTE, params: { id: item.employee?.id } }">{{ fullName(item.employee) }}</router-link></p>
                <p class="dash-list-meta">{{ prettify(item.type) }}<template v-if="item.effectiveDate"> · effective {{ fmtDate(item.effectiveDate) }}</template></p>
              </div>
              <div class="dash-list-side"><span class="dash-badge badge-soft-gray">{{ prettify(item.status) }}</span></div>
            </li>
          </ul>
          <p v-else class="sub-empty">No offboarding in progress.</p>
        </sectionCard>
      </div>
      <div class="col-lg-4">
        <sectionCard title="Upcoming" icon="pi-star" noPadding fill :actionTo="{ name: HOLIDAYS_ROUTE }" actionLabel="Holidays">
          <div class="sub-section-title">Holidays · next 60 days</div>
          <ul v-if="holidays.length" class="dash-list">
            <li v-for="holiday in holidays" :key="holiday.id + holiday.date" class="dash-list-item">
              <span class="date-block">
                <span class="date-block-day">{{ dayOfMonth(holiday.date) }}</span>
                <span class="date-block-month">{{ monthShort(holiday.date) }}</span>
              </span>
              <div class="dash-list-main">
                <p class="dash-list-title">{{ holiday.title }}</p>
                <p class="dash-list-meta">{{ holiday.type === 'organization' ? 'Organization holiday' : 'Public holiday' }}</p>
              </div>
              <div class="dash-list-side"><span class="dash-badge badge-soft-purple">{{ whenLabel(holiday.daysUntil) }}</span></div>
            </li>
          </ul>
          <p v-else class="sub-empty">No holidays in the next 60 days.</p>
          <div class="sub-section-title">Work anniversaries · {{ stats?.period?.label || 'this month' }}</div>
          <ul v-if="headcount.anniversaries.length" class="dash-list">
            <li v-for="item in headcount.anniversaries" :key="item.employee.id + item.date" class="dash-list-item">
              <employeeAvatar :employee="item.employee" :size="34" />
              <div class="dash-list-main">
                <p class="dash-list-title"><router-link :to="{ name: EMPLOYEE_GENERAL_ROUTE, params: { id: item.employee.id } }">{{ fullName(item.employee) }}</router-link></p>
                <p class="dash-list-meta">{{ item.years }} year{{ item.years === 1 ? '' : 's' }} · {{ fmtDate(item.date) }}</p>
              </div>
              <div class="dash-list-side"><span :class="['dash-badge', item.isToday ? 'badge-soft-green' : 'badge-soft-gray']">{{ item.isToday ? 'Today 🎉' : `${item.years}y` }}</span></div>
            </li>
          </ul>
          <p v-else class="sub-empty">No work anniversaries this month.</p>
        </sectionCard>
      </div>
      <div class="col-lg-4">
        <sectionCard title="Recent hires" icon="pi-user-plus" noPadding fill :actionTo="{ name: EMPLOYEES_ROUTE }" actionLabel="Employees">
          <ul v-if="headcount.recentHires.length" class="dash-list">
            <li v-for="item in headcount.recentHires" :key="item.employee.id" class="dash-list-item">
              <employeeAvatar :employee="item.employee" :size="36" />
              <div class="dash-list-main">
                <p class="dash-list-title"><router-link :to="{ name: EMPLOYEE_GENERAL_ROUTE, params: { id: item.employee.id } }">{{ fullName(item.employee) }}</router-link></p>
                <p class="dash-list-meta">{{ item.employee.position || 'No position' }}<template v-if="item.employee.department"> · {{ item.employee.department }}</template></p>
              </div>
              <div class="dash-list-side">
                <span class="value">{{ fmtDate(item.hireDate) }}</span>
                <span class="muted">{{ hiredAgo(item.hireDate) }}</span>
              </div>
            </li>
          </ul>
          <emptyState v-else icon="pi-user-plus" title="No hires recorded" message="Employees with a hire date will appear here." />
        </sectionCard>
      </div>
    </div>

    <!-- Row 6: projects -->
    <div v-if="hasProjects && projects.total" class="row">
      <div class="col-md-3 col-sm-6">
        <statCard label="Active projects" :value="projects.active" :sub="`${projects.total} total`" icon="pi-briefcase" tint="teal" :to="{ name: PROJECTS_ROUTE }" />
      </div>
      <div class="col-md-3 col-sm-6">
        <statCard label="Overdue milestones" :value="projects.overdueMilestones" sub="past due date" icon="pi-flag" :tint="projects.overdueMilestones ? 'red' : 'gray'" :to="{ name: PROJECTS_ROUTE }" />
      </div>
      <div class="col-md-3 col-sm-6">
        <statCard label="Outstanding invoices" :value="compactMoney(projects.invoices.outstandingAmount)" :sub="`${projects.invoices.outstandingCount} invoice${projects.invoices.outstandingCount === 1 ? '' : 's'} sent`" icon="pi-file" tint="blue" :to="{ name: PROJECTS_ROUTE }" />
      </div>
      <div class="col-md-3 col-sm-6">
        <statCard label="Overdue invoices" :value="compactMoney(projects.invoices.overdueAmount)" :sub="`${projects.invoices.overdueCount} overdue`" icon="pi-exclamation-triangle" :tint="projects.invoices.overdueCount ? 'red' : 'gray'" :to="{ name: PROJECTS_ROUTE }" />
      </div>
    </div>
  </div>
</template>

<script>
import VueApexCharts from 'vue3-apexcharts';
import attendanceCheckinCard from '@/components/dashboard/Attendance_Checkin_Card.vue';
import sectionCard from '@/components/dashboard/Dashboard_Section_Card.vue';
import statCard from '@/components/dashboard/Dashboard_Stat_Card.vue';
import emptyState from '@/components/dashboard/Dashboard_Empty_State.vue';
import employeeAvatar from '@/components/dashboard/Employee_Avatar.vue';
import approveLeaveRequestModal from '@/components/leave_request/Approve_Leave_Request_Modal.vue';
import rejectLeaveRequestModal from '@/components/leave_request/Reject_Leave_Request_Modal.vue';
import { GET_ADMIN_DASHBOARD_STATS } from '@/store/action_types';
import {
  ADD_JOB_POST_ROUTE,
  ATTENDANCE_ROUTE,
  ATTENDANCE_SETTING_ROUTE,
  EMPLOYEE_CONTRACTS_ROUTE,
  EMPLOYEE_GENERAL_ROUTE,
  EMPLOYEE_OFFBOARDING_ROUTE,
  EMPLOYEES_ROUTE,
  HOLIDAYS_ROUTE,
  JOB_POST_DETAILS_ROUTE,
  LEAVES_ROUTE,
  ORGANIZATION_STRUCTURE_ROUTE,
  PAYROLL_ROUTE,
  PROJECTS_ROUTE,
  RECRUITMENT_ROUTE,
  REGISTER_EMPLOYEE_ROUTE,
  REPORTS_ROUTE,
} from '@/router/routes';
import { MONTHS_ABBREV } from '@/utils/const';
import { PlatformModule } from '@/utils/enums';

const EMPTY_TODAY = { scheduled: 0, expected: 0, present: 0, onTime: 0, late: 0, onLeave: 0, absent: 0, notYetIn: 0, attendanceRate: 0, punctualityRate: 0 };

export default {
  name: 'AdminDashboard',
  components: {
    apexchart: VueApexCharts,
    attendanceCheckinCard,
    sectionCard,
    statCard,
    emptyState,
    employeeAvatar,
    approveLeaveRequestModal,
    rejectLeaveRequestModal,
  },
  data() {
    return {
      ADD_JOB_POST_ROUTE,
      ATTENDANCE_ROUTE,
      ATTENDANCE_SETTING_ROUTE,
      EMPLOYEE_CONTRACTS_ROUTE,
      EMPLOYEE_GENERAL_ROUTE,
      EMPLOYEE_OFFBOARDING_ROUTE,
      EMPLOYEES_ROUTE,
      HOLIDAYS_ROUTE,
      JOB_POST_DETAILS_ROUTE,
      LEAVES_ROUTE,
      ORGANIZATION_STRUCTURE_ROUTE,
      PAYROLL_ROUTE,
      PROJECTS_ROUTE,
      RECRUITMENT_ROUTE,
      REGISTER_EMPLOYEE_ROUTE,
      REPORTS_ROUTE,
      now: new Date(),
      exceptionTab: 'absent',
      showApproveModal: false,
      showRejectModal: false,
      selectedLeaveRequest: null,
    };
  },
  computed: {
    account() {
      return this.$store.getters.account.data;
    },
    modules() {
      return this.$store.getters.enabledModules;
    },
    hasAttendance() {
      return this.modules.includes(PlatformModule.attendance);
    },
    hasLeave() {
      return this.modules.includes(PlatformModule.leave);
    },
    hasPayroll() {
      return this.modules.includes(PlatformModule.payroll);
    },
    hasRecruitment() {
      return this.modules.includes(PlatformModule.recruitment);
    },
    hasProjects() {
      return this.modules.includes(PlatformModule.projectManagement);
    },
    /** "Who is out today" tabs the organization can show; attendance ones first. */
    exceptionTabs() {
      const tabs = [];
      if (this.hasAttendance) tabs.push('absent', 'late');
      if (this.hasLeave) tabs.push('leave');
      return tabs;
    },
    currentExceptionTab() {
      return this.exceptionTabs.includes(this.exceptionTab) ? this.exceptionTab : this.exceptionTabs[0] || 'absent';
    },
    /** Row 3 holds "who is out", payroll and "needs attention"; columns share the width evenly. */
    row3Col() {
      const cards = 1 + (this.hasPayroll ? 1 : 0) + (this.hasAttendance || this.hasLeave ? 1 : 0);
      return cards === 3 ? 'col-lg-4' : cards === 2 ? 'col-lg-6' : 'col-lg-12';
    },
    row4Col() {
      return this.hasRecruitment ? 'col-lg-4' : 'col-lg-6';
    },
    stats() {
      return this.$store.getters.adminDashboardStats.data;
    },
    isLoading() {
      return this.$store.getters.adminDashboardStats.isLoading;
    },
    greeting() {
      const hour = this.now.getHours();
      if (hour < 12) return 'Good morning';
      if (hour < 17) return 'Good afternoon';
      return 'Good evening';
    },
    headcount() {
      return this.stats?.headcount ?? {
        total: 0, active: 0, inactive: 0, newHiresThisMonth: 0, exitsThisMonth: 0, netChange: 0, withAttendanceShift: 0,
        byDepartment: [], byGender: { male: 0, female: 0, unspecified: 0 }, byContractType: [], recentHires: [], anniversaries: [],
      };
    },
    attendance() {
      return this.stats?.attendance ?? {
        isAttendanceDay: false, todayHoliday: null, today: EMPTY_TODAY, trend: [],
        month: { presenceRate: 0, punctualityRate: 0 }, absentToday: [], absentCount: 0, lateToday: [], lateCount: 0,
      };
    },
    leave() {
      return this.stats?.leave ?? { pending: 0, approvedThisMonth: 0, rejectedThisMonth: 0, onLeaveToday: 0, pendingRequests: [], onLeaveTodayList: [], byType: [] };
    },
    payroll() {
      return this.stats?.payroll ?? { currentPeriod: null, previousPeriod: null, netChangePct: null, trend: [], employeesWithoutSalary: 0 };
    },
    recruitment() {
      return this.stats?.recruitment ?? { openJobPosts: 0, totalApplications: 0, pendingApplications: 0, interviewStage: 0, accepted: 0, newThisWeek: 0, posts: [] };
    },
    offboarding() {
      return this.stats?.offboarding ?? { pending: 0, completedThisMonth: 0, list: [] };
    },
    contracts() {
      return this.stats?.contracts ?? { expiringCount: 0, expiring: [] };
    },
    projects() {
      return this.stats?.projects ?? { total: 0, active: 0, overdueMilestones: 0, invoices: { outstandingCount: 0, outstandingAmount: 0, overdueCount: 0, overdueAmount: 0 } };
    },
    holidays() {
      return this.stats?.holidays?.upcoming ?? [];
    },
    exceptionOverflow() {
      if (this.currentExceptionTab === 'absent') return this.attendance.absentCount > this.attendance.absentToday.length;
      if (this.currentExceptionTab === 'late') return this.attendance.lateCount > this.attendance.lateToday.length;
      return this.leave.onLeaveToday > this.leave.onLeaveTodayList.length;
    },
    attentionItems() {
      if (!this.stats) return [];
      const items = [];
      if (this.hasLeave && this.leave.pending) {
        items.push({ key: 'leave', icon: 'pi-calendar-plus', tint: 'amber', count: this.leave.pending, title: 'Leave requests awaiting decision', meta: 'Approve or reject pending requests', to: { name: LEAVES_ROUTE } });
      }
      if (this.hasPayroll && this.payroll.employeesWithoutSalary) {
        items.push({ key: 'salary', icon: 'pi-wallet', tint: 'red', count: this.payroll.employeesWithoutSalary, title: 'Employees without salary setup', meta: 'They will be skipped when payroll is generated', to: { name: PAYROLL_ROUTE } });
      }
      if (this.contracts.expiringCount) {
        items.push({ key: 'contracts', icon: 'pi-file', tint: 'amber', count: this.contracts.expiringCount, title: 'Contracts ending within 30 days', meta: 'Renew or plan offboarding', to: { name: EMPLOYEES_ROUTE } });
      }
      if (this.offboarding.pending) {
        items.push({ key: 'offboarding', icon: 'pi-user-minus', tint: 'purple', count: this.offboarding.pending, title: 'Offboarding checklists in progress', meta: 'Complete outstanding checklist items', to: { name: EMPLOYEES_ROUTE } });
      }
      const withoutShift = this.headcount.active - this.headcount.withAttendanceShift;
      if (this.hasAttendance && withoutShift > 0) {
        items.push({ key: 'shift', icon: 'pi-clock', tint: 'blue', count: withoutShift, title: 'Employees without an attendance shift', meta: 'They cannot check in until a shift is assigned', to: { name: ATTENDANCE_SETTING_ROUTE } });
      }
      if (this.hasRecruitment && this.recruitment.pendingApplications) {
        items.push({ key: 'applications', icon: 'pi-briefcase', tint: 'teal', count: this.recruitment.pendingApplications, title: 'Pending job applications', meta: `${this.recruitment.newThisWeek} arrived this week`, to: { name: RECRUITMENT_ROUTE, query: { tab: 'applications', status: 'pending' } } });
      }
      if (this.hasProjects && this.projects.invoices.overdueCount) {
        items.push({ key: 'invoices', icon: 'pi-exclamation-triangle', tint: 'red', count: this.projects.invoices.overdueCount, title: 'Overdue client invoices', meta: `${this.compactMoney(this.projects.invoices.overdueAmount)} outstanding`, to: { name: PROJECTS_ROUTE } });
      }
      if (this.hasProjects && this.projects.overdueMilestones) {
        items.push({ key: 'milestones', icon: 'pi-flag', tint: 'amber', count: this.projects.overdueMilestones, title: 'Project milestones past due', meta: 'Review project timelines', to: { name: PROJECTS_ROUTE } });
      }
      return items;
    },
    attendanceTrendOptions() {
      return {
        chart: { type: 'bar', stacked: true, toolbar: { show: false }, fontFamily: 'inherit' },
        colors: ['#2E7918', '#D97706', '#DC2626', '#2563EB'],
        plotOptions: { bar: { borderRadius: 4, columnWidth: '42%' } },
        dataLabels: { enabled: false },
        xaxis: {
          categories: this.attendance.trend.map((d) => `${d.dayLabel} ${d.date.slice(8)}`),
          axisBorder: { show: false },
          axisTicks: { show: false },
          labels: { style: { colors: '#6b7280', fontSize: '11px' } },
        },
        yaxis: { labels: { style: { colors: '#6b7280', fontSize: '11px' } }, forceNiceScale: true },
        grid: { borderColor: '#F0F0F2', strokeDashArray: 4 },
        legend: { position: 'top', horizontalAlign: 'right', fontSize: '11px', markers: { radius: 12 } },
        tooltip: { shared: true, intersect: false },
        states: { hover: { filter: { type: 'none' } } },
      };
    },
    attendanceTrendSeries() {
      const trend = this.attendance.trend;
      return [
        { name: 'On time', data: trend.map((d) => Math.max(0, d.present - d.late)) },
        { name: 'Late', data: trend.map((d) => d.late) },
        { name: 'Absent', data: trend.map((d) => d.absent) },
        { name: 'On leave', data: trend.map((d) => d.onLeave) },
      ];
    },
    departmentChartHeight() {
      return Math.max(180, this.headcount.byDepartment.length * 34 + 40);
    },
    departmentChartOptions() {
      return {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit' },
        colors: ['#2E7918'],
        plotOptions: { bar: { horizontal: true, borderRadius: 4, barHeight: '55%' } },
        dataLabels: { enabled: true, offsetX: 18, style: { fontSize: '11px', colors: ['#374151'] } },
        xaxis: { categories: this.headcount.byDepartment.map((d) => d.name), labels: { show: false }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { labels: { style: { fontSize: '11px', colors: '#374151' }, maxWidth: 140 } },
        grid: { show: false, padding: { left: 0, right: 24 } },
        tooltip: { y: { title: { formatter: () => 'Employees' } } },
      };
    },
    departmentChartSeries() {
      return [{ name: 'Employees', data: this.headcount.byDepartment.map((d) => d.count) }];
    },
    genderChartOptions() {
      return {
        chart: { type: 'donut', fontFamily: 'inherit' },
        labels: ['Male', 'Female', 'Unspecified'],
        colors: ['#2563EB', '#7C3AED', '#C4C8D0'],
        legend: { position: 'right', fontSize: '11px', markers: { radius: 12 } },
        dataLabels: { enabled: false },
        stroke: { width: 0 },
        plotOptions: {
          pie: {
            donut: {
              size: '72%',
              labels: {
                show: true,
                name: { fontSize: '11px', color: '#6b7280' },
                value: { fontSize: '18px', fontWeight: 700, color: '#18181b' },
                total: { show: true, label: 'Employees', fontSize: '11px', color: '#6b7280' },
              },
            },
          },
        },
      };
    },
    genderChartSeries() {
      const g = this.headcount.byGender;
      return [g.male ?? 0, g.female ?? 0, g.unspecified ?? 0];
    },
    payrollTrendOptions() {
      return {
        chart: { type: 'bar', toolbar: { show: false }, fontFamily: 'inherit', sparkline: { enabled: false } },
        colors: ['#A7D28F', '#2E7918'],
        plotOptions: { bar: { borderRadius: 3, columnWidth: '55%' } },
        dataLabels: { enabled: false },
        xaxis: { categories: this.payroll.trend.map((p) => this.shortPeriod(p.label)), labels: { style: { colors: '#6b7280', fontSize: '10px' } }, axisBorder: { show: false }, axisTicks: { show: false } },
        yaxis: { labels: { formatter: (v) => this.compactMoney(v, false), style: { colors: '#6b7280', fontSize: '10px' } } },
        grid: { borderColor: '#F0F0F2', strokeDashArray: 4, padding: { left: 0, right: 0 } },
        legend: { position: 'top', horizontalAlign: 'right', fontSize: '10px', markers: { radius: 12 } },
        tooltip: { y: { formatter: (v) => this.money(v) } },
      };
    },
    payrollTrendSeries() {
      return [
        { name: 'Gross', data: this.payroll.trend.map((p) => Math.round(p.gross)) },
        { name: 'Net', data: this.payroll.trend.map((p) => Math.round(p.net)) },
      ];
    },
  },
  methods: {
    asLocalDate(value) {
      if (typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) return `${value}T00:00:00`;
      return value;
    },
    fmtDate(value) {
      return this.$filters.getDateOnlyInNumberFormat(this.asLocalDate(value));
    },
    loadStats() {
      return this.$store.dispatch(GET_ADMIN_DASHBOARD_STATS, { organizationId: this.account.organizationId });
    },
    openApprove(request) {
      this.selectedLeaveRequest = request;
      this.showApproveModal = true;
    },
    openReject(request) {
      this.selectedLeaveRequest = request;
      this.showRejectModal = true;
    },
    onLeaveDecided() {
      this.showApproveModal = false;
      this.showRejectModal = false;
      this.loadStats();
    },
    fullName(employee) {
      if (!employee) return 'Unknown';
      return `${this.$filters.capitalize(employee.fname ?? '')} ${this.$filters.capitalize(employee.lname ?? '')}`.trim();
    },
    money(value) {
      return this.$filters.formatPrice(value ?? 0, 'ETB');
    },
    compactMoney(value, withCurrency = true) {
      const n = Number(value ?? 0);
      const formatted = new Intl.NumberFormat('en-US', { notation: 'compact', maximumFractionDigits: 1 }).format(n);
      return withCurrency ? `ETB ${formatted}` : formatted;
    },
    prettify(value) {
      if (!value) return '';
      return String(value)
        .replace(/[-_]/g, ' ')
        .replace(/\b\w/g, (c) => c.toUpperCase());
    },
    percentOf(part, total) {
      if (!total) return 0;
      return Math.round((part / total) * 100);
    },
    minutesLabel(mins) {
      if (mins < 60) return `${mins} min`;
      const h = Math.floor(mins / 60);
      const m = mins % 60;
      return m > 0 ? `${h}h ${m}m` : `${h}h`;
    },
    scheduleTime(time) {
      if (!time) return '—';
      return this.$filters.formatTimeToHHMM(this.$filters.toLocalHHMMSS(time));
    },
    shortPeriod(label) {
      if (!label) return '';
      const [month, year] = String(label).split(' ');
      return `${(month ?? '').slice(0, 3)} ${String(year ?? '').slice(-2)}`;
    },
    payrollStatusClass(status) {
      return { approved: 'badge-soft-green', generated: 'badge-soft-teal', pending: 'badge-soft-amber' }[status] ?? 'badge-soft-gray';
    },
    daysUntil(dateValue) {
      if (!dateValue) return null;
      const target = new Date(dateValue);
      const targetUTC = Date.UTC(target.getUTCFullYear(), target.getUTCMonth(), target.getUTCDate());
      const now = new Date();
      const todayUTC = Date.UTC(now.getFullYear(), now.getMonth(), now.getDate());
      return Math.round((targetUTC - todayUTC) / 86400000);
    },
    whenLabel(days) {
      if (days === null || days === undefined) return '';
      if (days <= 0) return 'Today';
      if (days === 1) return 'Tomorrow';
      return `in ${days} days`;
    },
    backOnLabel(endDate) {
      const days = this.daysUntil(endDate);
      if (days === null) return 'soon';
      if (days <= 0) return 'tomorrow';
      return `in ${days + 1} day${days + 1 === 1 ? '' : 's'}`;
    },
    hiredAgo(hireDate) {
      const days = -this.daysUntil(hireDate);
      if (days <= 0) return 'Today';
      if (days === 1) return 'Yesterday';
      if (days < 30) return `${days} days ago`;
      const months = Math.round(days / 30);
      if (months < 12) return `${months} mo ago`;
      const years = Math.round(days / 365);
      return `${years} yr${years === 1 ? '' : 's'} ago`;
    },
    formatRange(start, end) {
      const s = this.fmtDate(start);
      const e = this.fmtDate(end);
      return s === e ? s : `${s} → ${e}`;
    },
    dayOfMonth(dateStr) {
      return new Date(dateStr).getUTCDate();
    },
    monthShort(dateStr) {
      return MONTHS_ABBREV[new Date(dateStr).getUTCMonth()];
    },
  },
  mounted() {
    this.loadStats();
  },
};
</script>

<style scoped>
.kpi-icon-sm {
  width: 34px;
  height: 34px;
  border-radius: 9px;
}

.kpi-icon-sm .pi {
  font-size: 0.85rem;
}

.today-strip {
  margin-top: 14px;
  border-top: 1px solid #F0F0F2;
  padding-top: 12px;
}

.today-strip-title {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin-bottom: 8px;
}

.today-strip-title .pi {
  font-size: 0.7rem;
  margin-right: 4px;
  color: #2E7918;
}

.today-strip-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.today-cell {
  background: #FAFAFA;
  border: 1px solid #F0F0F2;
  border-radius: 10px;
  padding: 8px 6px;
  text-align: center;
}

.today-cell .today-n {
  display: block;
  font-size: 1.1rem;
  font-weight: 700;
  color: #18181b;
  line-height: 1.1;
}

.today-cell .today-l {
  display: block;
  font-size: 0.66rem;
  color: #6b7280;
  margin-top: 3px;
}

.dash-note {
  display: flex;
  align-items: center;
  gap: 10px;
  padding: 10px 14px;
  border-radius: 10px;
  background: #eff6ff;
  border: 1px solid #bfdbfe;
  font-size: 0.82rem;
  color: #1e3a8a;
  margin-bottom: 1rem;
}

.dash-note .pi {
  color: #2563eb;
}

.dash-note a {
  color: #2563eb;
  font-weight: 600;
}

.mini-tabs {
  display: inline-flex;
  background: #F3F4F6;
  border-radius: 8px;
  padding: 3px;
  gap: 2px;
}

.mini-tab {
  border: none;
  background: transparent;
  font-size: 0.72rem;
  font-weight: 600;
  color: #6b7280;
  padding: 4px 10px;
  border-radius: 6px;
  cursor: pointer;
  white-space: nowrap;
}

.mini-tab span {
  margin-left: 4px;
  font-size: 0.66rem;
  background: #ffffff;
  color: #374151;
  padding: 1px 6px;
  border-radius: 999px;
}

.mini-tab.active {
  background: #ffffff;
  color: #2E7918;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.06);
}

.payroll-hero {
  display: flex;
  justify-content: space-between;
  align-items: flex-start;
  margin-bottom: 12px;
}

.payroll-label {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
}

.payroll-value {
  font-size: 1.5rem;
  font-weight: 700;
  color: #18181b;
  letter-spacing: -0.02rem;
  line-height: 1.2;
}

.payroll-meta {
  font-size: 0.75rem;
  color: #6b7280;
  margin-top: 4px;
  display: flex;
  align-items: center;
}

.payroll-grid {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 8px;
}

.payroll-cell {
  background: #FAFAFA;
  border: 1px solid #F0F0F2;
  border-radius: 8px;
  padding: 8px 6px;
  text-align: center;
}

.payroll-cell .l {
  display: block;
  font-size: 0.65rem;
  color: #6b7280;
  text-transform: uppercase;
  letter-spacing: 0.04em;
}

.payroll-cell .v {
  display: block;
  font-size: 0.85rem;
  font-weight: 700;
  color: #18181b;
  margin-top: 2px;
  white-space: nowrap;
}

.text-warning-dark {
  color: #b45309;
}

.text-warning-dark .pi {
  margin-right: 4px;
}

.composition-title {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  margin: 10px 0 8px 0;
}

.dash-progress-row.compact {
  margin-bottom: 10px;
}

.strip-inset {
  padding: 1rem 1.25rem 0.5rem 1.25rem;
}

.sub-section-title {
  font-size: 0.72rem;
  font-weight: 600;
  text-transform: uppercase;
  letter-spacing: 0.05em;
  color: #6b7280;
  padding: 10px 1.25rem 4px 1.25rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
}

.sub-section-title .count {
  background: #F3F4F6;
  color: #374151;
  border-radius: 999px;
  padding: 1px 8px;
  font-size: 0.68rem;
}

.sub-empty {
  font-size: 0.78rem;
  color: #9ca3af;
  padding: 6px 1.25rem 12px 1.25rem;
  margin: 0;
}

.date-block {
  width: 42px;
  height: 42px;
  border-radius: 10px;
  background: #f5f3ff;
  color: #7c3aed;
  display: inline-flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  line-height: 1;
}

.date-block-day {
  font-size: 1rem;
  font-weight: 700;
}

.date-block-month {
  font-size: 0.62rem;
  text-transform: uppercase;
  font-weight: 600;
  margin-top: 2px;
}

.btn-sm {
  font-size: 0.78rem;
  padding: 5px 12px;
}

@media (max-width: 1400px) {
  .today-strip-grid,
  .payroll-grid {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
}
</style>
